import { z } from 'zod';
import { NearbyAssetSchema, NearbyAircraftSchema } from './dispatch';
import { IncidentHazardTypeSchema, IncidentStatusSchema } from './incident';

/**
 * Evacuation Logistics Surface (§4.4) — the assembled, incident-scoped read model
 * the operator-cop-doctrine calls "the demo-winning Logistics value" (§2.5 / §4.4).
 *
 * It ASSEMBLES existing objects (no new data sources): the W.1 community headcount
 * + wellness picture (§4.25), the R.4.2 staging areas (§4.18), the §4.5 response-
 * asset registry (aviation bases) + §4.6 live ADS-B aircraft (airlift hulls), and
 * the §4.24/E.1 CAP alerts overlapping the linked communities (the road-washout /
 * weather context). One GET assembles all five so the panel is a pure render.
 *
 * "Linked communities" are those with at least one headcount or wellness check tied
 * to this incident (the same linkage the SITREP community rollup uses) — there is no
 * direct community→incident column.
 */

/** One community linked to the incident, with its latest incident-tied headcount +
 *  wellness status and a centroid for fly-to. */
export const EvacuationCommunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  community_type: z.string(),
  lon: z.number(),
  lat: z.number(),
  population: z.number().nullable(),
  vulnerable_population: z.number().nullable(),
  evacuated: z.number().nullable(),
  remaining: z.number().nullable(),
  accounted_for: z.number().nullable(),
  unreachable: z.number().nullable(),
  /** #462 — split of `remaining` awaiting an aircraft out (northern air-evac).
   *  NULL = not broken out / unknown (≠ 0 = none awaiting a flight). */
  awaiting_airlift: z.number().nullable(),
  /** #462 — the honest accounting state of THIS community for THIS incident:
   *   'counted'  — an incident-tied headcount exists; the numbers are real.
   *   'baseline' — only a pre-incident baseline snapshot exists (#125 fallback);
   *                figures are shown but flagged (NOT this evacuation's accounting).
   *   'pending'  — the community is in an evacuation zone for this incident but has
   *                NO recorded headcount yet (incident-tied OR baseline). The counts
   *                are ABSENT (null) — "not yet counted", NEVER rendered as zero.
   *  UNKNOWN ≠ ZERO (life-safety): only 'counted' contributes a real evacuated/
   *  remaining picture; 'pending' communities are the incompleteness the aggregate
   *  MUST surface and never imply away as "complete / 0 remaining". */
  accounting_state: z.enum(['counted', 'baseline', 'pending']),
  /** Effective time of the latest headcount surfaced (ISO-8601), or null. */
  headcount_as_of: z.string().nullable(),
  /** True when the headcount figures above come from the community's pre-incident
   *  baseline snapshot (incident_id IS NULL) rather than one tied to this incident
   *  — the §4.30/#125 fallback so a linked community never reads empty before an
   *  operator records the first incident-tied count. */
  headcount_is_baseline: z.boolean(),
  wellness_status: z.string().nullable(),
  warming_centres_active: z.number().nullable(),
  vulnerable_residents_unreachable: z.number().nullable(),
});
export type EvacuationCommunity = z.infer<typeof EvacuationCommunitySchema>;

/** The live ICS-209 block-31 rollup across every linked community. */
export const EvacuationAggregateSchema = z.object({
  community_count: z.number().int().nonnegative(),
  total_population: z.number().int().nonnegative(),
  total_vulnerable_population: z.number().int().nonnegative(),
  total_evacuated: z.number().int().nonnegative(),
  total_remaining: z.number().int().nonnegative(),
  total_accounted_for: z.number().int().nonnegative(),
  total_unreachable: z.number().int().nonnegative(),
  /** #462 — sum of the KNOWN awaiting-air-lift counts (communities with a recorded,
   *  non-null awaiting_airlift). NOT a sum over unknowns — a community whose
   *  awaiting_airlift is null contributes nothing and is not implied to be 0. This
   *  is what the First Nation air-evac seat reads to answer "how many still awaiting
   *  a flight." */
  total_awaiting_airlift: z.number().int().nonnegative(),
  /** #462 (gate fix) — how many COUNTED communities still have people remaining but no
   *  recorded airlift split (awaiting_airlift null). When > 0, total_awaiting_airlift is a
   *  FLOOR, not a complete count — the UI must show "N unknown" and never render the total
   *  as a definitive 0 (unknown ≠ zero on the airlift metric, the same life-safety rule). */
  awaiting_airlift_unknown_count: z.number().int().nonnegative(),
  /** #462 — how many in-scope (evacuation-zone) communities are 'pending': in a zone
   *  for this incident but with NO recorded headcount yet. When > 0 the accounting is
   *  INCOMPLETE — the progress rollup MUST NOT read as "complete / all accounted for"
   *  (unknown≠zero, the false-all-clear guard). */
  pending_community_count: z.number().int().nonnegative(),
  wellness_complete: z.number().int().nonnegative(),
  wellness_in_progress: z.number().int().nonnegative(),
  wellness_planned: z.number().int().nonnegative(),
  wellness_gap_identified: z.number().int().nonnegative(),
  total_warming_centres_active: z.number().int().nonnegative(),
  /** How many linked communities are showing baseline (pre-incident) headcount
   *  figures because no incident-tied count exists yet (#125 transparency). */
  baseline_headcount_count: z.number().int().nonnegative(),
});
export type EvacuationAggregate = z.infer<typeof EvacuationAggregateSchema>;

/** A staging area declared on this incident, with its live staged-resource count. */
export const EvacuationStagingAreaSchema = z.object({
  id: z.string(),
  name: z.string(),
  lon: z.number().nullable(),
  lat: z.number().nullable(),
  capacity_assets: z.number().nullable(),
  capacity_personnel: z.number().nullable(),
  /** Count of resource requests currently staged here (lifecycle = staging). */
  assigned_request_count: z.number().int().nonnegative(),
  closed_at: z.string().nullable(),
});
export type EvacuationStagingArea = z.infer<typeof EvacuationStagingAreaSchema>;

/** An active CAP alert overlapping a linked community (road-washout / weather). */
export const EvacuationCapAlertSchema = z.object({
  id: z.string(),
  event: z.string().nullable(),
  severity: z.string().nullable(),
  headline: z.string().nullable(),
  area_desc: z.string().nullable(),
  expires_at: z.string().nullable(),
});
export type EvacuationCapAlert = z.infer<typeof EvacuationCapAlertSchema>;

export const EvacuationSurfaceSchema = z.object({
  incident: z.object({
    id: z.string(),
    incident_number: z.string(),
    name: z.string(),
    hazard_type: IncidentHazardTypeSchema,
    status: IncidentStatusSchema,
    lon: z.number(),
    lat: z.number(),
  }),
  communities: z.array(EvacuationCommunitySchema),
  aggregate: EvacuationAggregateSchema,
  staging_areas: z.array(EvacuationStagingAreaSchema),
  /** §4.5 aviation bases within reach of the incident centroid (nearest first). */
  aviation_bases: z.array(NearbyAssetSchema),
  /** §4.6 live aircraft within reach; airlift hulls flagged via is_airlift. */
  aircraft: z.array(NearbyAircraftSchema),
  cap_alerts: z.array(EvacuationCapAlertSchema),
});
export type EvacuationSurface = z.infer<typeof EvacuationSurfaceSchema>;
